import React from 'react';
import axios from 'axios';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';

import type { Client } from '../features/dashboard/types/dashboard';

type SearchResult = {
  id: string;
  name: string;
  typeName: string; // "Insured" | "Prospect" | "Policy"
  type: number;
  policyNumber?: string | null;
  phone?: string | null;
  policyEffectiveDate?: string | null;
  policyExpirationDate?: string | null;
  displayName: string;
};

type NowCertsInsuredDetail = {
  databaseId: string;
  commercialName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  addressLine1?: string | null;
  stateNameOrAbbreviation?: string | null;
  state?: string | null;
  city?: string | null;
  zipCode?: string | null;
  eMail?: string | null;
  eMail2?: string | null;
  eMail3?: string | null;
  phone?: string | null;
  cellPhone?: string | null;
  primaryLocationDatabaseId?: string | null;
  [key: string]: unknown;
};

export type SelectClientProps = {
  value: string;
  onChange: (value: string) => void;
  onClientSelected?: (client: Client) => void;
};

const TYPE_CONFIG: Record<string, { badge: string; badgeBg: string; badgeColor: string; borderColor: string }> = {
  Insured:  { badge: 'I',   badgeBg: '#f8eeff', badgeColor: '#A200FF', borderColor: '#e6b3ff' },
  Prospect: { badge: 'P',   badgeBg: '#f8eeff', badgeColor: '#A200FF', borderColor: '#e6b3ff' },
  Policy:   { badge: 'POL', badgeBg: '#f0ddff', badgeColor: '#8500D0', borderColor: '#e6b3ff' },
};

function deriveDisplayName(item: SearchResult): string {
  if (item.typeName === 'Policy') return item.policyNumber || item.name;
  return item.name;
}

type StatusMsg = { text: string; type: 'info' | 'error' } | null;

export default function SelectClient({ value, onChange, onClientSelected }: SelectClientProps) {
  const [suggestions, setSuggestions] = React.useState<SearchResult[]>([]);
  const [resolving, setResolving] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState<StatusMsg>(null);
  const searchTimeoutRef = React.useRef<number | null>(null);
  const dismissTimerRef = React.useRef<number | null>(null);

  const showStatus = (text: string, type: 'info' | 'error') => {
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    setStatusMsg({ text, type });
    dismissTimerRef.current = window.setTimeout(() => setStatusMsg(null), 3500);
  };

  const searchClients = (e: AutoCompleteCompleteEvent) => {
    const query = e.query || '';
    onChange(query);
    setStatusMsg(null);

    if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = window.setTimeout(async () => {
      if (!query.trim()) { setSuggestions([]); return; }
      if (query.trim().length < 3) { setSuggestions([]); setStatusMsg({ text: 'Search at least 3 characters to get insured or policies', type: 'info' }); return; }

      try {
        const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-search`, {
          params: { searchText: query },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
            'Content-Type': 'application/json',
            NCToken: localStorage.getItem('NCToken') || '',
          },
        });

        const raw: any[] = Array.isArray(res.data) ? res.data : [];
        const list: SearchResult[] = raw.map((item) => {
          const result: SearchResult = {
            id: item.id,
            name: item.name || '',
            typeName: item.typeName || 'Insured',
            type: item.type ?? 0,
            policyNumber: item.policyNumber ?? null,
            phone: item.phone || item.cellPhone || null,
            policyEffectiveDate: item.policyEffectiveDate ?? null,
            policyExpirationDate: item.policyExpirationDate ?? null,
            displayName: '',
          };
          result.displayName = deriveDisplayName(result);
          return result;
        });
        setSuggestions(list.length > 0 ? list : [{ id: '__no_results__', name: '', typeName: '__empty__', type: -1, policyNumber: null, phone: null, displayName: '' }]);
      } catch {
        setSuggestions([{ id: '__no_results__', name: '', typeName: '__empty__', type: -1, policyNumber: null, phone: null, displayName: '' }]);
      }
    }, 400);
  };

  const fetchInsuredDetails = async (databaseId: string): Promise<NowCertsInsuredDetail | null> => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/customer-by-id`, {
        params: { insuredDatabaseId: databaseId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
          'Content-Type': 'application/json',
          NCToken: localStorage.getItem('NCToken') || '',
        },
      });
      const raw = res.data?.value?.[0] ?? (Array.isArray(res.data) ? res.data[0] : res.data);
      return raw ?? null;
    } catch {
      return null;
    }
  };

  const resolveInsuredIdFromPolicy = async (policy: SearchResult): Promise<string | null> => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-by-policy-id`, {
        params: { policyId: policy.id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
          'Content-Type': 'application/json',
          NCToken: localStorage.getItem('NCToken') || '',
        },
      });
      return res.data?.insuredDatabaseId ?? null;
    } catch {
      return null;
    }
  };

  const buildClient = (item: SearchResult, detail: NowCertsInsuredDetail): Client => {
    const firstLast = [detail.firstName ?? '', detail.lastName ?? '']
      .map((p) => p?.trim())
      .filter(Boolean)
      .join(' ');
    const city = detail.city ?? '';
    const state = detail.stateNameOrAbbreviation ?? detail.state ?? '';
    const zip = detail.zipCode ?? '';
    return {
      id: detail.databaseId,
      name: detail.commercialName || item.name,
      typeLabel: 'Insured',
      primaryContactName: firstLast || item.name,
      addressLine1: detail.addressLine1 ?? '',
      addressLine2: city && state && zip ? `${city}, ${state} ${zip}` : undefined,
      city,
      state,
      zip,
      phone: detail.phone || detail.cellPhone || item.phone || '',
      email: detail.eMail || detail.eMail2 || detail.eMail3 || '',
      primaryLocationDatabaseId: detail.primaryLocationDatabaseId ?? undefined,
    };
  };

  const handleSelect = async (item: SearchResult | null) => {
    if (!item || item.typeName === '__empty__') return;

    onChange('');
    if (!onClientSelected) return;

    setResolving(true);
    setStatusMsg(null);

    try {
      let databaseId: string | null = item.typeName === 'Policy'
        ? await resolveInsuredIdFromPolicy(item)
        : item.id;

      if (!databaseId) {
        showStatus(
          item.typeName === 'Policy'
            ? `No client found for policy "${item.policyNumber || item.name}"`
            : 'Client not found. Please try again.',
          'info'
        );
        return;
      }

      const detail = await fetchInsuredDetails(databaseId);
      if (!detail) {
        showStatus('Could not load client details. Please try again.', 'error');
        return;
      }

      onClientSelected(buildClient(item, detail));
    } catch {
      showStatus('Something went wrong. Please try again.', 'error');
    } finally {
      setResolving(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
      if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const itemTemplate = (item: SearchResult) => {
    if (item.typeName === '__empty__') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 4px', color: '#6b7280', pointerEvents: 'none', cursor: 'default' }}>
          <i className="pi pi-info-circle" style={{ fontSize: 13, color: '#9ca3af' }} />
          <span style={{ fontSize: 13 }}>No clients or policies found</span>
        </div>
      );
    }
    const conf = TYPE_CONFIG[item.typeName] ?? TYPE_CONFIG.Insured;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1f2937', padding: '2px 0', width: '100%' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 26,
            height: 20,
            borderRadius: 10,
            padding: '0 5px',
            background: conf.badgeBg,
            color: conf.badgeColor,
            fontWeight: 700,
            fontSize: item.typeName === 'Policy' ? 9 : 12,
            border: `1px solid ${conf.borderColor}`,
            flexShrink: 0,
            letterSpacing: item.typeName === 'Policy' ? 0.4 : 0,
          }}
          title={item.typeName}
        >
          {conf.badge}
        </span>

        {/* Left: name info */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          {item.typeName === 'Policy' ? (
            <>
              <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 13 }}>{item.policyNumber || '—'}</span>
              <span style={{ color: '#6b7280', fontSize: 11 }}>{item.name}</span>
            </>
          ) : (
            <span style={{ color: '#1f2937' }}>{item.name}</span>
          )}
        </div>

        {/* Right: dates pill — Policy only */}
        {item.typeName === 'Policy' && (item.policyEffectiveDate || item.policyExpirationDate) && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3,
            flexShrink: 0, borderLeft: '1px solid #e5e7eb', paddingLeft: 10,
          }}>
            {item.policyEffectiveDate && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#16a34a', background: '#dcfce7', borderRadius: 4, padding: '1px 5px', letterSpacing: 0.2 }}>EFF</span>
                <span style={{ fontSize: 10, color: '#374151', fontWeight: 500 }}>
                  {new Date(item.policyEffectiveDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                </span>
              </span>
            )}
            {item.policyExpirationDate && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#dc2626', background: '#fee2e2', borderRadius: 4, padding: '1px 5px', letterSpacing: 0.2 }}>EXP</span>
                <span style={{ fontSize: 10, color: '#374151', fontWeight: 500 }}>
                  {new Date(item.policyExpirationDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
        <AutoComplete
          value={value}
          suggestions={suggestions}
          completeMethod={searchClients}
          field="displayName"
          onChange={(e) => {
            if (e.value && typeof e.value === 'object' && (e.value as SearchResult).typeName === '__empty__') return;
            onChange(typeof e.value === 'string' ? e.value : '');
          }}
          onSelect={(e) => handleSelect(e.value as SearchResult)}
          placeholder="Search for another customer or policy..."
          className="app-header__searchAuto"
          inputClassName="app-header__searchInput"
          panelClassName="app-header__searchPanel"
          itemTemplate={itemTemplate}
          emptyMessage="No clients or policies found"
          disabled={resolving}
        />
        {resolving && (
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'inline-flex',
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: 14, color: '#A200FF' }} />
          </span>
        )}
      </div>

      {statusMsg && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 9999,
            background: statusMsg.type === 'error' ? '#fff3f3' : '#f0f6ff',
            border: `1px solid ${statusMsg.type === 'error' ? '#f5c6c6' : '#e6b3ff'}`,
            borderRadius: 6,
            padding: '7px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            whiteSpace: 'nowrap',
          }}
        >
          <i
            className={statusMsg.type === 'error' ? 'pi pi-times-circle' : 'pi pi-info-circle'}
            style={{ fontSize: 13, color: statusMsg.type === 'error' ? '#ba170b' : '#A200FF', flexShrink: 0 }}
          />
          <span style={{ fontSize: 12, color: statusMsg.type === 'error' ? '#ba170b' : '#381574', fontWeight: 500 }}>
            {statusMsg.text}
          </span>
        </div>
      )}
    </div>
  );
}
